import React, { useState, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Environment } from '@react-three/drei';
import { FaUndo, FaRedo, FaSave, FaShoppingCart } from 'react-icons/fa';
import SofaModel from '../components/SofaModel';
import './CustomStudio.css';

const CustomStudio = () => {
  const [config, setConfig] = useState({
    baseModel: 'classic',
    configuration: '3-seater',
    fabric: 'performance-linen-beige',
    legStyle: 'mid-century',
    legFinish: 'walnut',
    cushionFill: 'standard'
  });

  const [showSaveDialog, setShowSaveDialog] = useState(false);

  const baseModels = [
    { id: 'classic', name: 'The Classic', description: 'Timeless elegance' },
    { id: 'modernist', name: 'The Modernist', description: 'Clean lines' },
    { id: 'lounger', name: 'The Lounger', description: 'Deep comfort' }
  ];

  const configurations = [
    { id: '2-seater', name: '2-Seater' },
    { id: '3-seater', name: '3-Seater' },
    { id: 'l-shape-left', name: 'L-Shape (Left)' },
    { id: 'l-shape-right', name: 'L-Shape (Right)' }
  ];

  const fabrics = [
    { id: 'performance-linen-beige', name: 'Performance Linen', color: '#E5DCC3' },
    { id: 'velvet-terracotta', name: 'Velvet', color: '#C07A5F' },
    { id: 'velvet-forest', name: 'Velvet', color: '#2F4F4F' },
    { id: 'leather-cognac', name: 'Leather', color: '#8B5A3C' },
    { id: 'boucle-cream', name: 'Bouclé', color: '#F5F5DC' }
  ];

  const legStyles = [
    { id: 'mid-century', name: 'Mid-Century Tapered' },
    { id: 'modern-metal', name: 'Modern Metal' },
    { id: 'classic-wood', name: 'Classic Wood' }
  ];

  const legFinishes = [
    { id: 'walnut', name: 'Walnut', color: '#5C4033' },
    { id: 'oak', name: 'Oak', color: '#C19A6B' },
    { id: 'chrome', name: 'Chrome', color: '#C0C0C0' },
    { id: 'black', name: 'Black', color: '#2D2D2D' }
  ];

  const cushionFills = [
    { id: 'standard', name: 'Standard Foam' },
    { id: 'down-feather', name: 'Down Feather' }
  ];

  const updateConfig = (key, value) => {
    setConfig({ ...config, [key]: value });
  };

  const handleSaveDesign = () => {
    setShowSaveDialog(true);
  };

  const handleAddToCart = () => {
    // Add to cart logic here
    alert('Design added to cart!');
  };

  return (
    <div className="custom-studio">
      <div className="studio-container">
        {/* Left Side - 3D View */}
        <div className="studio-stage">
          <div className="stage-controls">
            <button className="control-btn" aria-label="Undo">
              <FaUndo />
            </button>
            <button className="control-btn" aria-label="Redo">
              <FaRedo />
            </button>
          </div>

          <div className="model-viewer">
            <Canvas shadows>
              <PerspectiveCamera makeDefault position={[5, 3, 5]} fov={50} />
              <OrbitControls 
                enablePan={false}
                minPolarAngle={Math.PI / 4}
                maxPolarAngle={Math.PI / 2}
                minDistance={4}
                maxDistance={10}
              />
              
              {/* Lighting */}
              <ambientLight intensity={0.5} />
              <directionalLight
                position={[10, 10, 5]}
                intensity={1}
                castShadow
                shadow-mapSize-width={1024}
                shadow-mapSize-height={1024}
              />
              <pointLight position={[-10, 10, -10]} intensity={0.3} />
              
              {/* Environment for reflections */}
              <Environment preset="apartment" />
              
              {/* 3D Sofa Model */}
              <Suspense fallback={null}>
                <SofaModel config={config} />
              </Suspense>
              
              {/* Ground plane */}
              <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
                <planeGeometry args={[20, 20]} />
                <shadowMaterial opacity={0.2} />
              </mesh>
            </Canvas>
            <div className="canvas-overlay">
              <div className="rotate-hint">
                <span>↻</span>
                <p>Click and drag to rotate</p>
              </div>
            </div>
          </div>

          <div className="view-options">
            <button className="view-btn">Front View</button>
            <button className="view-btn">Side View</button>
            <button className="view-btn">Top View</button>
          </div>
        </div>

        {/* Right Side - Configuration Panel */}
        <div className="studio-panel">
          <div className="panel-header">
            <div className="panel-actions">
              <button className="action-btn" onClick={handleSaveDesign}>
                <FaSave /> Save Design
              </button>
              <button className="action-btn primary" onClick={handleAddToCart}>
                <FaShoppingCart /> Add to Cart
              </button>
            </div>
          </div>

          <div className="panel-content">
            {/* Step 1: Base Model */}
            <div className="config-section">
              <h3 className="section-title">1. Choose Base Model</h3>
              <div className="option-grid">
                {baseModels.map(model => (
                  <div
                    key={model.id}
                    className={`option-card ${config.baseModel === model.id ? 'selected' : ''}`}
                    onClick={() => updateConfig('baseModel', model.id)}
                  >
                    <h4>{model.name}</h4>
                    <p className="option-desc">{model.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 2: Configuration */}
            <div className="config-section">
              <h3 className="section-title">2. Configuration & Size</h3>
              <div className="option-grid">
                {configurations.map(conf => (
                  <div
                    key={conf.id}
                    className={`option-card ${config.configuration === conf.id ? 'selected' : ''}`}
                    onClick={() => updateConfig('configuration', conf.id)}
                  >
                    <h4>{conf.name}</h4>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 3: Fabric */}
            <div className="config-section">
              <h3 className="section-title">3. Fabric & Color</h3>
              <div className="fabric-grid">
                {fabrics.map(fabric => (
                  <div
                    key={fabric.id}
                    className={`fabric-swatch ${config.fabric === fabric.id ? 'selected' : ''}`}
                    onClick={() => updateConfig('fabric', fabric.id)}
                    title={fabric.name}
                  >
                    <div 
                      className="swatch-color"
                      style={{ backgroundColor: fabric.color }}
                    ></div>
                    <div className="swatch-info">
                      <span className="swatch-name">{fabric.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 4: Leg Style */}
            <div className="config-section">
              <h3 className="section-title">4. Leg Style & Finish</h3>
              <div className="option-list">
                {legStyles.map(leg => (
                  <label
                    key={leg.id}
                    className={`option-radio ${config.legStyle === leg.id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="legStyle"
                      value={leg.id}
                      checked={config.legStyle === leg.id}
                      onChange={(e) => updateConfig('legStyle', e.target.value)}
                    />
                    <span className="radio-label">{leg.name}</span>
                  </label>
                ))}
              </div>

              <div className="finish-options">
                <p className="subsection-label">Finish</p>
                <div className="finish-grid">
                  {legFinishes.map(finish => (
                    <div
                      key={finish.id}
                      className={`finish-option ${config.legFinish === finish.id ? 'selected' : ''}`}
                      onClick={() => updateConfig('legFinish', finish.id)}
                    >
                      <div 
                        className="finish-color"
                        style={{ backgroundColor: finish.color }}
                      ></div>
                      <span>{finish.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Step 5: Cushion Fill */}
            <div className="config-section">
              <h3 className="section-title">5. Cushion Fill</h3>
              <div className="option-list">
                {cushionFills.map(cushion => (
                  <label
                    key={cushion.id}
                    className={`option-radio ${config.cushionFill === cushion.id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="cushionFill"
                      value={cushion.id}
                      checked={config.cushionFill === cushion.id}
                      onChange={(e) => updateConfig('cushionFill', e.target.value)}
                    />
                    <span className="radio-label">{cushion.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="modal-overlay" onClick={() => setShowSaveDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Save Your Design</h2>
            <p>Your design has been saved! You can access it from your account.</p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowSaveDialog(false)}>
                Close
              </button>
              <button className="btn btn-primary" onClick={() => { setShowSaveDialog(false); handleAddToCart(); }}>
                Save & Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomStudio;